import { CatalogItem, List, ListItem } from "@/types/supplier";
import { popAlertOk } from "@/functions/alert";
import { t } from "@/plugins/vue-i18n";
import { actionSheetController, ActionSheetOptions, modalController } from "@ionic/vue";
import { interactionFeedbackToast } from "@/functions/baseToast";
import handleOfflineAction from "@/functions/offlineMode";
import { OFFLINE_ACTION } from "@/types/enum/general";
import { AddInformationItemParams, GetListItemsParams } from "@/types/list";
import { SORT_METHOD } from "@/types/enum/propEnum";
import { usePersistentStore } from "@/stores/persistent";
import { useListStore } from "@/stores/lists";
import { useSupplierStore } from "@/stores/suppliers";
import { useRouteParams } from "@/composables/useRouteParams";

export function useProductModal(listId?: string, options?: { offset?: number }) {
    const { locationId } = useRouteParams()
    const persistentStore = usePersistentStore()
    const listStore = useListStore()
    const supplierStore = useSupplierStore()

    const addItem = async (listId: string, amount: number, product: CatalogItem): Promise<void> => {
        if (persistentStore.offlineMode) {
            const itemId = 'new-' + new Date().getTime().toString()

            if (!listStore.listItems[listId].data?.find(item => item.item.id === product.id)) {
                const newListItem: ListItem = {
                    id: itemId,
                    item: product,
                    listId,
                    quantity: amount,
                    supplierItemId: product.id,
                    piecePrice: 0,
                    targetStock: 0
                }

                listStore.listItems[listId].data?.push(newListItem)
            } else {
                interactionFeedbackToast(false, {
                    type: 'returnType.ItemAlreadyInList',
                    id: 'add-product-list'
                }, {
                    cssClass: `toast-offset-70`
                }, true)
                return
            }

            modalController.dismiss()

            handleOfflineAction(OFFLINE_ACTION.ADD_ITEM, {
                locationId: locationId.value,
                listId,
                itemId,
                amount: amount,
                supplierItemId: product.id
            })
            return
        }

        modalController.dismiss()

        const response = await listStore.addListItem(locationId.value, listId, product?.id, amount)

        interactionFeedbackToast(
            response,
            {
                id: 'product-add-list',
                type: 'addList',
                target: 'product'
            },
            {
                cssClass: `toast-offset-${options?.offset ?? 50}`
            },
            true
        )

        if (!response) return

        listStore.isLoaded.listItems = false

        await listStore.getListItems(new GetListItemsParams({
            locationId: locationId.value,
            listId,
            searchKey: supplierStore.searchQuery !== '' ? supplierStore.searchQuery : undefined,
            // category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
            tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
            withQuantity: listStore.listFilter.withQuantity,
            sortDesc: (supplierStore.sortDesc === SORT_METHOD.ALPH_DESC),
            take: 50
        }))

        listStore.isLoaded.listItems = true

        await listStore.getListItems(new GetListItemsParams({
            locationId: locationId.value,
            listId,
            searchKey: supplierStore.searchQuery !== '' ? supplierStore.searchQuery : undefined,
            // category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
            tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
            withQuantity: listStore.listFilter.withQuantity,
            sortDesc: (supplierStore.sortDesc === SORT_METHOD.ALPH_DESC),
            take: 0
        }))
    }

    const addItemInformation = async (params: AddInformationItemParams): Promise<boolean> => {
        const response = await listStore.addInformationItem(params)

        interactionFeedbackToast(
            response,
            {
                id: 'product-add-list',
                type: 'addList',
                target: 'product'
            },
            {
                cssClass: `toast-offset-${options?.offset ?? 50}`
            },
            true
        )

        if (!response) return false

        listStore.isLoaded.listItems = false

        await listStore.getListItems(new GetListItemsParams({
            locationId: locationId.value,
            listId: params.listId,
            searchKey: supplierStore.searchQuery !== '' ? supplierStore.searchQuery : undefined,
            // category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
            tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
            withQuantity: listStore.listFilter.withQuantity,
            sortDesc: (supplierStore.sortDesc === SORT_METHOD.ALPH_DESC),
            take: 50
        }))

        listStore.isLoaded.listItems = true

        await listStore.getListItems(new GetListItemsParams({
            locationId: locationId.value,
            listId: params.listId,
            searchKey: supplierStore.searchQuery !== '' ? supplierStore.searchQuery : undefined,
            // category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
            tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
            withQuantity: listStore.listFilter.withQuantity,
            sortDesc: (supplierStore.sortDesc === SORT_METHOD.ALPH_DESC),
            take: 0
        }))

        return true
    }

    const createListDialog = async (listList: List[], amount: number, product: CatalogItem) => {
        const params: ActionSheetOptions = {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            header: t('alert.selectList'),
            buttons: [],
            cssClass: 'custom-action-sheet',
            mode: 'ios'
        }

        for (const list of listList) {
            if (list.id) params.buttons.push({
                text: list.name,
                role: 'select',
                handler: () => {
                    if (!list.id) return false
                    addItem(list.id, amount, product)
                }
            })
        }

        params.buttons.push({
            text: t('button.cancel'),
            role: 'cancel',
            data: {
                action: 'cancel'
            }
        })

        const actionSheet = await actionSheetController.create(params);
        await actionSheet.present();
    }

    const addList = async (product: CatalogItem, amount: number, supplierId: string): Promise<void> => {
        if (listId) return await addItem(listId, amount, product)

        const listList = listStore.lists.data.filter((list: List) => list.supplierId === supplierId)

        if (listList.length === 0 && supplierId) return await popAlertOk({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            message: t('alert.noList')
        })

        if (listList.length === 1) {
            const list = listList[0]
            if (!list?.id) return
            return addItem(list.id, amount, product)
        }

        await createListDialog(listList, amount, product)
    }

    const createListDialogInformation = async (listList: List[], amount: number, informationId: string, productId: string, noAutoSelect?: boolean) => {
        const params: ActionSheetOptions = {
            header: t('alert.selectList'),
            buttons: [],
            cssClass: 'custom-action-sheet',
            mode: 'ios'
        }

        if (listList.length === 1 && !noAutoSelect) return addItemInformation({
            locationId: locationId.value,
            listId: listList[0].id ?? '',
            quantity: amount,
            informationId,
            productId
        })

        for (const list of listList) {
            if (list.id) params.buttons.push({
                text: list.name,
                role: 'select',
                handler: async () => {
                    if (!list.id) return false
                    addItemInformation({
                        locationId: locationId.value,
                        listId: list.id,
                        quantity: amount,
                        informationId,
                        productId
                    })
                    if (await modalController.getTop()) modalController.dismiss()
                    return true
                }
            })
        }

        params.buttons.push({
            text: t('button.cancel'),
            role: 'cancel',
            data: {
                action: 'cancel'
            }
        })

        const actionSheet = await actionSheetController.create(params);
        await actionSheet.present();
    }

    const addListInformation = async (productId: string, informationId: string, amount: number, supplierId: string): Promise<boolean> => {
        if (listId) return await addItemInformation({
            listId,
            quantity: amount,
            informationId,
            productId,
            locationId: locationId.value
        })

        const listList = listStore.lists.data.filter((list: List) => list.supplierId === supplierId)

        if (listList.length === 0 && supplierId) {
            await popAlertOk({
                message: t('alert.noList')
            })
            return false
        }

        if (listList.length === 1) {
            const list = listList[0]
            if (!list?.id) return false
            return addItemInformation({
                listId: list.id,
                quantity: amount,
                informationId,
                productId,
                locationId: locationId.value
            })
        }

        await createListDialogInformation(listList, amount, informationId, productId)

        return true
    }

    return {
        addList,
        addItem,
        createListDialog,
        createListDialogInformation,
        addListInformation
    }
}
